import * as React from 'react';
import { getPromise } from 'fingerprintjs2';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import Loading from '../../../commonComponents/Loading';
import { getReferenceFromRefereeToken, completeReferenceAsReferee } from '../../../core/api';
import { clearRefereeTokenInLocalStorage } from '../../../core/session';
import { ChildReference } from '../../../models/be/Reference';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import SecondaryButton from '../../common/buttons/SecondaryButton';
import RefereePageLayout from '../../layouts/RefereePageLayout';
import RequesterNotesForReferee from '../../common/RequesterNotesForReferee';
import ReadOnlyQuestion from './ReadOnlyQuestion';
import './RefereeReview.less';
import { Session } from '../../../models/be/transport/Session';
import MessageBox from '../../common/MessageBox';
import { getRefereeTokenFromQueryOrLocalStorage } from '../../../core/domain';
import { statuses } from '../../../lib/ApiBase';
import RefereeExpiryPage from '../../common/RefereeExpiryPage';
import { SaveAndFinishLater } from '../refereeSaved/RefereeSaved';
import Buttons from '../../common/Buttons';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import { hasValue } from '../../../lib/utils';

const RefereeReview = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(true);
	const [refereeToken, setRefereeToken] = React.useState<string>();
	const [notesValidationMessage, setNotesValidationMessage] = React.useState<string>();
	const [childReference, setChildReference] = React.useState<ChildReference>();
	const [showExpiryPage, setShowExpiryPage] = React.useState(false);
	const [session, setSession] = React.useState<Session>();

	React.useEffect(() => {
		// TODO: Once all pages are updated to new css, should update css for body and remove following line.
		// Add following line to reuse the css.
		document.body.classList.add('dataCapturePage');

		performAsyncAction(async () => {
			const [newRefereeToken, tokenIsValid] = await getRefereeTokenFromQueryOrLocalStorage(location);
			if (tokenIsValid) {
				try {
					const newReference = await getReferenceFromRefereeToken(newRefereeToken);
					setChildReference(newReference.childReference);
					// getPromise returns a full browser fingerprint based on the users browser, we obfuscate it by encoding it as base64 before transporting it to the API
					setSession({ item1: btoa(JSON.stringify(await getPromise())) });
				} catch (e) {
					if (e.status === statuses.unauthorized) {
						setShowExpiryPage(true);
					} else {
						throw e;
					}
				}
			} else {
				setShowExpiryPage(true);
			}
			setRefereeToken(newRefereeToken);
		});

		return () => {
			document.body.classList.remove('dataCapturePage');
		};
	}, []);

	const handleSubmitButtonClick = async () => {
		const newReference = await getReferenceFromRefereeToken(refereeToken);
		if (!newReference.childReference.refereeNotesAccept && newReference.childReference.notesToReferee) {
			setNotesValidationMessage('Consent required');
			return;
		}
		await performAsyncAction(async () => {
			await completeReferenceAsReferee(refereeToken, true, session, false);
			clearRefereeTokenInLocalStorage();
			navigate('/referee/thanks');
		});
	};

	const handleBackButtonClick = async () => {
		navigate('/referee/questions');
	};

	const handleEditButton = (questionNumber: number) => () => {
		navigate(`/referee/questions#${questionNumber}`);
	};

	const handleNavigateToPage = async (pageSlug: string) => {
		navigate(`/referee/${pageSlug}`);
	};

	if (hasValue(errorMessage)) return <MessageBox error={errorMessage} />;

	if (showExpiryPage) {
		return <RefereeExpiryPage expiredRefereeToken={refereeToken} />;
	}

	if (loading) return <Loading />;

	// This has to be after showExpiryPage and loading.
	if (location.search.indexOf('refereeToken') !== -1) {
		navigate(location.pathname, { replace: true });
		return null;
	}

	const reference = childReference;
	return (
		<RefereePageLayout
			navigateToPage={handleNavigateToPage}
			className="refereeReviewPage"
			title="Review"
			currentFlowProgressBarName="Review"
		>
			{notesValidationMessage && <NotesValidationMessage>Consent required</NotesValidationMessage>}
			{reference && (
				<>
					<p className="pageIntroduction">Please review the provided information before submitting.</p>
					<RequesterNotesForReferee
						notes={childReference?.notesToReferee}
						consentToNotes={childReference?.refereeNotesAccept}
					/>
					<section className="readOnlyQuestions">
						{reference.refereeQuestions?.map((question, i) => {
							return (
								<React.Fragment key={question.text}>
									{i !== 0 && <hr />}
									<QuestionFieldContainer>
										<ReadOnlyQuestion
											refereeQuestion={question}
											refereeAnswer={reference.refereeAnswers ? reference.refereeAnswers[i] : ''}
											indexInQuestionArray={i}
											onEditButtonClick={handleEditButton(i + 1)}
										/>
									</QuestionFieldContainer>
								</React.Fragment>
							);
						})}
					</section>
					<Buttons>
						<SecondaryButton text="Back" onClick={handleBackButtonClick} />
						<PrimaryButton text="Submit" onClick={handleSubmitButtonClick} />
					</Buttons>
					<SaveAndFinishLater refereeToken={refereeToken} currentPagePath={location.pathname} />
				</>
			)}
		</RefereePageLayout>
	);
};

export default RefereeReview;

const NotesValidationMessage = styled.div`
	background-color: #ffe7db;
	border: 1px solid #c23a00;
	color: #c23a00;
	margin: 8px 0;
	width: 100%;
	position: relative;
	padding: 8px;
	border-radius: 4px;
	text-align: left;
`;

const QuestionFieldContainer = styled.div`
	span {
		white-space: pre-wrap;
	}
`;
